import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Calendar,
  Target,
  Users,
  TrendingUp,
  Share2,
  Heart,
  Eye,
  Clock,
  Link as LinkIcon,
} from "lucide-react";
import DOMPurify from "dompurify";
import { Link, useParams } from "react-router-dom";
import Notification from "../../components/UI/Notification";
import { formatDistanceToNow } from "date-fns";

const CampaignDetails = () => {
  const { id } = useParams();
  const campaignId = id || 1;
  const [showOptions, setShowOptions] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [viewCount, setViewCount] = useState(1247);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);
  const updatedAt = campaign?.updated_at;
  const timeAgo = updatedAt
    ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
    : "";
  const campaignUrl = `${window.location.origin}/campaign/${campaignId}`;

  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true);
      setError("");

      try {
        const { data } = await axios.get(
          `https://hrmsnew.prepstripe.com/api/public/campaigns/${campaignId}`,
        );

        if (data.success && data.data) {
          setCampaign(data.data);
        } else {
          throw new Error(data.message || "Failed to fetch campaign data");
        }
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to fetch campaign details. Please try again later.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  // copy to clipboard button
  const handleCopy = async (campaign_id) => {
    const domain = window.location.origin;

    try {
      await navigator.clipboard.writeText(`${domain}/campaign/${campaign_id}`);
      setCopied(true);
      setNotification({
        message: "Campaign link copied to clipboard!",
        type: "success",
      });

      setTimeout(() => {
        setCopied(false);
        setNotification("");
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      setNotification("Failed to copy the link. Please try again.");
    }
  };

  const handleLinkedInShare = () => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      campaignUrl,
    )}`;
    window.open(linkedinUrl, "_blank");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatSalary = (from, to) => {
    return `₹${parseInt(from).toLocaleString()} - ₹${parseInt(to).toLocaleString()}`;
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-12 h-12 bg-white/10 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-pink-900 to-orange-800 flex items-center justify-center">
        <div className="text-center p-8 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-white/80 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-all duration-300 border border-white/20 hover:scale-105"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 rounded-2xl border border-white/20">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-black mb-2">
            Campaign Not Found
          </h2>
          <p className="text-black">
            The requested campaign could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    // rgb(244, 244, 244);
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: "rgb(244, 244, 244)" }}
    >
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 border border-gray-300 shadow-md bg-white backdrop-blur-sm rounded-full">
            <Target className="w-4 h-4 text-cyan-300" />
            <span className="text-sm font-medium">Campaign Spotlight</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-4 leading-tight">
            {campaign?.campaign_name || "Campaign Details"}
          </h1>
          <div className="flex items-center justify-center gap-6 text-white/60 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Updated {timeAgo}</span>
            </div>
          </div>
        </div>

        {/* Main campaign card */}
        <div className="max-w-4xl mx-auto">
          <div
            className="backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-[1.02] bg-white"
            style={{
              border: "2px solid transparent",
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(to right, #a07cc4, #83d8e9) border-box",
            }}
          >
            {/* Hero section */}
            <div className="relative h-64 bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
              <div className="text-center z-10">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 mx-auto backdrop-blur-sm">
                  <TrendingUp className="w-10 h-10" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {campaign?.position}
                </h2>
                <p className=" mt-2 text-gray-200 font-semibold">
                  {campaign?.workplace}
                </p>
              </div>

              {/* Floating action buttons */}
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={handleLike}
                  className={`p-3 rounded-full bg-white backdrop-blur-sm border transition-all duration-300 hover:scale-110 ${
                    isLiked
                      ? "bg-red-500 text-white"
                      : "bg-white text-black hover:bg-gray-100"
                  }`}
                >
                  <Heart
                    className={`w-5 h-5 ${isLiked ? "fill-current text-red-500" : ""}`}
                  />
                </button>
                <div className="relative inline-block text-left">
                  <button
                    onClick={() => setShowOptions((prev) => !prev)}
                    className="p-3 rounded-full bg-white backdrop-blur-sm text-black hover:bg-gray-100 transition-all duration-300 hover:scale-110"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>

                  {showOptions && (
                    <div className="absolute right-0 z-10 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black/5">
                      <div className="py-1">
                        <button
                          onClick={() => handleCopy(campaign.id)}
                          className="flex items-center gap-2 w-full px-2 py-2 text-sm"
                        >
                          <LinkIcon className="w-4 h-4 mr-2" />
                          {copied ? "Copied" : "Copy Link"}
                        </button>
                        <button
                          onClick={handleLinkedInShare}
                          className="flex items-center gap-2 w-full px-2 py-2 text-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="icon icon-tabler icons-tabler-outline icon-tabler-brand-linkedin"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M8 11v5" />
                            <path d="M8 8v.01" />
                            <path d="M12 16v-5" />
                            <path d="M16 16v-3a2 2 0 1 0 -4 0" />
                            <path d="M3 7a4 4 0 0 1 4 -4h10a4 4 0 0 1 4 4v10a4 4 0 0 1 -4 4h-10a4 4 0 0 1 -4 -4z" />
                          </svg>
                          Share on LinkedIn
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content section */}
            <div className="p-8 space-y-8">
              {/* Campaign description */}
              <div className="pros dark:prose-invert space-y-4">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-cyan-400 to-purple-500 rounded-full"></div>
                  Job Description
                </h3>

                <div className="prose max-w-none dark:prose-invert ">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(campaign.job_description),
                    }}
                    className="dark:text-white"
                  />
                </div>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={`w-3 h-3 rounded-full ${campaign?.status === "Active" ? "bg-green-400" : "bg-gray-400"}`}
                    ></div>
                    <span className="font-semibold">Status</span>
                  </div>
                  <p className="text-2xl font-bold">{campaign?.status}</p>
                  <p className="text-sm">Current state</p>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105">
                  <Calendar className="w-8 h-8 text-black mb-3" />
                  <h4 className="font-semibold mb-1">Application Period</h4>
                  <p className="text-sm font-bold">
                    {formatDate(campaign?.from_date)}
                  </p>
                  <p className="text-sm">to {formatDate(campaign?.to_date)}</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105">
                  <Users className="w-8 h-8 text-black mb-3" />
                  <h4 className="font-semibold mb-1">Candidates</h4>
                  <p className="text-2xl font-bold">
                    {campaign?.candidates?.length || 0}
                  </p>
                  <p className="text-sm">Applied</p>
                </div>
              </div>

              {/* HR Contact Info */}
              {campaign?.user && (
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold flex items-center gap-3">
                    <div className="w-2 h-8 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-full"></div>
                    Contact Information
                  </h3>
                  <div className="bg-white/5 p-6 rounded-2xl border border-gray-400">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="font-bold text-lg text-white">
                          {campaign.user.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">
                          {campaign.user.name}
                        </h4>
                        <p className="">{campaign.user.role}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium mb-1">Email</dt>
                        <dd className="font-semibold">{campaign.user.email}</dd>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Call to action */}
              <div className="text-center pt-8">
                {/* {campaign?.application_form_id ? ( */}
                <Link
                  to={`${window.location.origin}/application-form/?campaign_id=${campaign.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-12 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold rounded-full hover:from-purple-600 hover:to-cyan-600 transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-purple-500/25 mr-4"
                >
                  Apply Now
                </Link>

                <p className="text-gray-800 mt-8">
                  Join our team and make an impact
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-10 text-gray-600">
          <p className="text-black">
            © 2025 HR Management System. Connecting talent with opportunity.
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CampaignDetails;
